// useArenaPhysics — Rapier world lifecycle for the Sports Arena.
//
// Owns:
//   • A single Rapier World (gravity, integration parameters)
//   • The dynamic ball body + collider (rebuilt when sport changes)
//   • Fixed colliders for the arena floor
//   • Per-frame physics tick (via useFrame)
//   • Goal detection (AABB test against ball centre each frame)
//   • Player-kick and NPC-kick impulse APIs
//   • Ball reset after a goal
//
// The rest of the campus scene lives OUTSIDE the physics world — NPCs walking
// the quad, buildings, trees, etc. don't have rigid bodies so the physics
// simulation stays cheap regardless of the crowd density.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

import { ARENA_CENTER, SPORTS, getSportCenter, BASKETBALL_COURT } from "./arenaState";

// Module-scoped init guard — Rapier's WASM is loaded exactly once per session.
let rapierReadyPromise = null;
function ensureRapierReady() {
  if (!rapierReadyPromise) {
    rapierReadyPromise = RAPIER.init().then(() => RAPIER);
  }
  return rapierReadyPromise;
}

const BALL_START_HEIGHT = 2.6;
const GOAL_COOLDOWN_MS = 1400;

/**
 * useArenaPhysics
 *
 * @param {object} args
 * @param {"basketball"|"soccer"} args.sport
 * @param {(who: "player"|"cpu") => void} args.onGoal   Score callback.
 * @returns {{
 *   ballRef: React.MutableRefObject<THREE.Mesh|null>,
 *   ready: boolean,
 *   kickBallFromPlayer: (playerPos: THREE.Vector3, aimTarget?: THREE.Vector3) => boolean,
 *   kickBallFromNpc: (npcPos: THREE.Vector3, targetGoalCenter: number[], impulseScale: number, jitter: number) => void,
 *   getBallPosition: () => THREE.Vector3,
 *   resetBall: () => void,
 * }}
 */
export function useArenaPhysics({ sport, onGoal }) {
  const [ready, setReady] = useState(false);
  const worldRef = useRef(null);
  const ballBodyRef = useRef(null);
  const ballRef = useRef(null); // three.Mesh
  const sportRef = useRef(sport);
  sportRef.current = sport;

  const lastGoalAtRef = useRef(0);
  const ballScratch = useRef(new THREE.Vector3());
  const dirScratch = useRef(new THREE.Vector3());

  // -------------------------------------------------------------------------
  // World init — runs once on mount.
  // -------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    ensureRapierReady().then((rapier) => {
      if (cancelled) return;
      if (worldRef.current) return;

      const world = new rapier.World({ x: 0, y: -12.5, z: 0 });
      // Slower physics timestep gives more forgiving arcs on light balls.
      world.timestep = 1 / 60;

      // Arena floor: three separate fixed colliders — one under each sport
      // field — so an errant ball can't collide with invisible geometry
      // outside the playable footprint of the active sport.
      const floorSpecs = [
        { center: getSportCenter("basketball"), hx: 12, hz: 8 },
        { center: getSportCenter("soccer"),     hx: 14, hz: 10 },
        { center: getSportCenter("football"),   hx: 18, hz: 11 },
        { center: getSportCenter("baseball"),   hx: 24, hz: 18 },
      ];
      for (const spec of floorSpecs) {
        const [ax, , az] = spec.center;
        const floorBodyDesc = rapier.RigidBodyDesc.fixed().setTranslation(
          ax,
          0.05,
          az
        );
        const floorBody = world.createRigidBody(floorBodyDesc);
        const floorColliderDesc = rapier.ColliderDesc.cuboid(spec.hx, 0.05, spec.hz)
          .setRestitution(0.35)
          .setFriction(0.9);
        world.createCollider(floorColliderDesc, floorBody);
      }

      worldRef.current = { world, rapier };

      // -------------------------------------------------------------------
      // Basketball hoop colliders — a static backboard slab + an 8-segment
      // ring of small cuboids approximating the RIM for each hoop. Fixes
      // the reported bug where scoring was AABB-only: the ball now bounces
      // off the backboard and rim like a real basketball, and only a shot
      // that clears the rim ring registers as a score (velGate + AABB in
      // the score loop still guards against non-downward false positives).
      //
      // Rim world math mirrors BasketballHoop in SportsArena.jsx:
      //   • group at [±hoopReach, 0, 0] relative to court centre
      //   • backboard mesh at local  [ side * 0.15, hoopHeight + 0.55, 0]
      //   • rim mesh at local        [-side * 0.15, hoopHeight,        0]
      // -------------------------------------------------------------------
      {
        const [cx, , cz] = getSportCenter("basketball");
        const hoopReach = BASKETBALL_COURT.hoopReach;   // 6.5
        const hoopHeight = BASKETBALL_COURT.hoopHeight; // 3.05
        for (const sign of [-1, 1]) {
          // BACKBOARD — 2.05 × 1.35 × 0.09 slab, upright, at the outer side
          const bbX = cx + sign * hoopReach + sign * 0.15;
          const bbY = hoopHeight + 0.55;
          const bbZ = cz;
          const bbDesc = rapier.RigidBodyDesc.fixed().setTranslation(bbX, bbY, bbZ);
          const bbBody = world.createRigidBody(bbDesc);
          const bbColl = rapier.ColliderDesc.cuboid(0.045, 0.675, 1.025) // half-extents
            .setRestitution(0.62)
            .setFriction(0.5);
          world.createCollider(bbColl, bbBody);

          // RIM — 8 short cuboid segments arranged in a ring at radius 0.42m
          const rimCenterX = cx + sign * hoopReach - sign * 0.15;
          const rimCenterZ = cz;
          const rimRadius = 0.42;
          const RIM_SEGMENTS = 8;
          for (let i = 0; i < RIM_SEGMENTS; i++) {
            const angle = (i / RIM_SEGMENTS) * Math.PI * 2;
            const rx = rimCenterX + Math.cos(angle) * rimRadius;
            const rz = rimCenterZ + Math.sin(angle) * rimRadius;
            // Segment tangent so the ball glances off the *outer* face
            const segLen = (2 * Math.PI * rimRadius) / RIM_SEGMENTS * 0.55;
            const segDesc = rapier.RigidBodyDesc.fixed().setTranslation(rx, hoopHeight, rz);
            const segBody = world.createRigidBody(segDesc);
            const segColl = rapier.ColliderDesc.cuboid(0.028, 0.06, segLen / 2)
              .setRestitution(0.55)
              .setFriction(0.6);
            // Rotate around Y so segment is tangent to the rim circle
            const q = { x: 0, y: Math.sin(-angle / 2), z: 0, w: Math.cos(-angle / 2) };
            world.createCollider(segColl.setRotation(q), segBody);
          }
        }
      }

      setReady(true);
    });
    return () => {
      cancelled = true;
      // World is intentionally left alive across unmount to avoid tearing down
      // WASM state when React StrictMode double-mounts. Real teardown only
      // fires on hard navigation away from the scene, at which point the
      // whole tab is disposed anyway.
    };
  }, []);

  // -------------------------------------------------------------------------
  // Ball body — recreated whenever the active sport changes.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!ready) return;
    const ctx = worldRef.current;
    if (!ctx) return;
    const { world, rapier } = ctx;

    // Tear down previous ball body if any.
    if (ballBodyRef.current) {
      try {
        world.removeRigidBody(ballBodyRef.current);
      } catch (e) {
        /* body already gone */
      }
      ballBodyRef.current = null;
    }

    const cfg = SPORTS[sport];
    const [ax, , az] = getSportCenter(sport);

    const bodyDesc = rapier.RigidBodyDesc.dynamic()
      .setTranslation(ax, BALL_START_HEIGHT, az)
      .setLinearDamping(cfg.ballLinearDamping)
      .setAngularDamping(0.6)
      .setCcdEnabled(true) // continuous collision detection prevents the ball
                           // from tunneling through the 0.12m-tall basketball
                           // rim segments on hard dunks.
      .setCanSleep(false);
    const body = world.createRigidBody(bodyDesc);

    const colliderDesc = rapier.ColliderDesc.ball(cfg.ballRadius)
      .setRestitution(cfg.ballRestitution)
      .setFriction(0.7)
      .setDensity(1.1);
    world.createCollider(colliderDesc, body);

    ballBodyRef.current = body;
    lastGoalAtRef.current = 0;
  }, [ready, sport]);

  // -------------------------------------------------------------------------
  // Public API — kicks, resets, position query
  // -------------------------------------------------------------------------
  const getBallPosition = useCallback(() => {
    if (!ballBodyRef.current) {
      const [cx, , cz] = getSportCenter(sportRef.current);
      ballScratch.current.set(cx, BALL_START_HEIGHT, cz);
    } else {
      const t = ballBodyRef.current.translation();
      ballScratch.current.set(t.x, t.y, t.z);
    }
    return ballScratch.current;
  }, []);

  const resetBall = useCallback(() => {
    const body = ballBodyRef.current;
    if (!body) return;
    const [ax, , az] = getSportCenter(sportRef.current);
    body.setTranslation({ x: ax, y: BALL_START_HEIGHT, z: az }, true);
    body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    body.setAngvel({ x: 0, y: 0, z: 0 }, true);
  }, []);

  const kickBallFromPlayer = useCallback((playerPos, aimTarget) => {
    const body = ballBodyRef.current;
    if (!body) return false;
    const cfg = SPORTS[sportRef.current];
    const t = body.translation();
    const ballPos = ballScratch.current.set(t.x, t.y, t.z);

    // Direction: base = ball-away-from-player (so the ball flies away from
    // whichever side the player is standing on). If the player passed an
    // aim target, blend 50/50 toward it so click-to-aim feels responsive.
    const base = dirScratch.current
      .set(ballPos.x - playerPos.x, 0, ballPos.z - playerPos.z);
    if (base.lengthSq() < 0.001) {
      // Player standing exactly on ball — shove toward CPU goal.
      base.set(1, 0, 0);
    }
    base.normalize();

    if (aimTarget) {
      const aimDir = new THREE.Vector3(
        aimTarget.x - ballPos.x,
        0,
        aimTarget.z - ballPos.z
      );
      if (aimDir.lengthSq() > 0.001) {
        aimDir.normalize();
        base.lerp(aimDir, 0.55).normalize();
      }
    }

    // Add a small upward arc so it visually reads as a shot, not a puck.
    const impulse = {
      x: base.x * cfg.playerKickImpulse,
      y: cfg.playerKickImpulse * 0.28,
      z: base.z * cfg.playerKickImpulse,
    };
    body.applyImpulse(impulse, true);
    // Add a bit of spin for style.
    body.applyTorqueImpulse(
      { x: base.z * 0.2, y: 0, z: -base.x * 0.2 },
      true
    );
    return true;
  }, []);

  const kickBallFromNpc = useCallback(
    (npcPos, targetGoalCenter, impulseScale, jitter) => {
      const body = ballBodyRef.current;
      if (!body) return;
      const cfg = SPORTS[sportRef.current];
      const t = body.translation();

      // Direction: from ball toward the target goal.
      const dir = dirScratch.current.set(
        targetGoalCenter[0] - t.x,
        0,
        targetGoalCenter[2] - t.z
      );
      if (dir.lengthSq() < 0.001) {
        dir.set(-1, 0, 0);
      }
      dir.normalize();

      // Apply random angular jitter (rotates the direction around Y).
      if (jitter > 0.0001) {
        const angle = (Math.random() - 0.5) * 2 * jitter;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const nx = dir.x * cos - dir.z * sin;
        const nz = dir.x * sin + dir.z * cos;
        dir.set(nx, 0, nz);
      }

      const power = cfg.npcKickImpulseBase * impulseScale;
      const impulse = {
        x: dir.x * power,
        y: power * 0.3,
        z: dir.z * power,
      };
      body.applyImpulse(impulse, true);
      body.applyTorqueImpulse(
        { x: dir.z * 0.15, y: 0, z: -dir.x * 0.15 },
        true
      );
    },
    []
  );

  /**
   * Apply a raw impulse to the local ball. Used by Match Invite so a remote
   * peer's kick can influence this client's ball simulation.
   *
   * @param {number[]} dir     Unit direction vector [x,y,z] on the XZ plane.
   * @param {number}   power   Impulse magnitude.
   */
  const applyRemoteKick = useCallback((dir, power) => {
    const body = ballBodyRef.current;
    if (!body) return;
    if (!Array.isArray(dir) || dir.length !== 3) return;
    const impulse = {
      x: dir[0] * power,
      y: Math.abs(power) * 0.28,
      z: dir[2] * power,
    };
    body.applyImpulse(impulse, true);
    body.applyTorqueImpulse(
      { x: dir[2] * 0.18, y: 0, z: -dir[0] * 0.18 },
      true
    );
  }, []);

  // Direct set of the ball's kinematics — used by baseball to control the
  // exact pitch/hit trajectory without going through the impulse APIs.
  const setBallState = useCallback((pos, vel, ang) => {
    const body = ballBodyRef.current;
    if (!body) return;
    if (pos) body.setTranslation({ x: pos.x, y: pos.y, z: pos.z }, true);
    if (vel) body.setLinvel({ x: vel.x, y: vel.y, z: vel.z }, true);
    if (ang) body.setAngvel({ x: ang.x, y: ang.y, z: ang.z }, true);
    else if (vel) body.setAngvel({ x: 0, y: 0, z: 0 }, true);
  }, []);

  // -------------------------------------------------------------------------
  // Per-frame tick: step world, sync ball mesh, detect goals.
  // -------------------------------------------------------------------------
  const insideBox = (p, box) =>
    p.x >= box.min[0] &&
    p.x <= box.max[0] &&
    p.y >= box.min[1] &&
    p.y <= box.max[1] &&
    p.z >= box.min[2] &&
    p.z <= box.max[2];

  useFrame(() => {
    const ctx = worldRef.current;
    if (!ctx) return;
    ctx.world.step();

    const body = ballBodyRef.current;
    if (!body) return;
    // Rapier can briefly return null for translation() on a body that's
    // being torn down while a sport swap re-creates the ball rigid body.
    // Bail out of this frame instead of crashing.
    const t = body.translation();
    if (!t || typeof t.x !== "number") return;
    if (ballRef.current) {
      ballRef.current.position.set(t.x, t.y, t.z);
      const r = body.rotation();
      if (r && typeof r.x === "number") {
        ballRef.current.quaternion.set(r.x, r.y, r.z, r.w);
      }
    }

    // Guard: if the ball flew out of the arena (crazy impulse or physics
    // glitch), snap it back to the active sport's centre.
    const [ax, , az] = getSportCenter(sportRef.current);
    if (
      Math.abs(t.x - ax) > 22 ||
      Math.abs(t.z - az) > 18 ||
      t.y < -3 ||
      t.y > 12
    ) {
      resetBall();
      return;
    }

    // Goal detection (with cooldown so a single goal never double-counts).
    const now = performance.now();
    if (now - lastGoalAtRef.current < GOAL_COOLDOWN_MS) return;

    const cfg = SPORTS[sportRef.current];
    const point = { x: t.x, y: t.y, z: t.z };
    // Basketball only: require the ball to be moving DOWNWARD when it
    // enters the rim's AABB. A rising shot that clips the top of the
    // scoring box mid-arc shouldn't count — the ball has to *fall through*
    // the hoop. Soccer / football goals are entered horizontally so no
    // velocity gate is applied.
    let velGate = true;
    if (sportRef.current === "basketball") {
      const v = body.linvel();
      velGate = v && typeof v.y === "number" && v.y < -0.2;
    }
    if (velGate && insideBox(point, cfg.cpuGoal)) {
      lastGoalAtRef.current = now;
      onGoal && onGoal("player");
      setTimeout(resetBall, 900);
    } else if (velGate && insideBox(point, cfg.playerGoal)) {
      lastGoalAtRef.current = now;
      onGoal && onGoal("cpu");
      setTimeout(resetBall, 900);
    }
  });

  return useMemo(
    () => ({
      ballRef,
      ready,
      kickBallFromPlayer,
      kickBallFromNpc,
      applyRemoteKick,
      getBallPosition,
      resetBall,
      setBallState,
    }),
    [ready, kickBallFromPlayer, kickBallFromNpc, applyRemoteKick, getBallPosition, resetBall, setBallState]
  );
}
